import {USER_KEYS} from "@/api/userApi";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import RefluencedLogo from "@/assets/images/refluenced.svg";
import Button from "@/components/Common/Button";
import InputField from "@/components/Common/forms/InputField";
import TextButton from "@/components/Common/TextButton";
import useCountdown from "@/hooks/useCountdown";
import {useZodState} from "@/hooks/useZodState";
import {client} from "@/httpClient";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {useTranslation} from "react-i18next";
import {useKeyPressEvent, useMedia, useTitle} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/_unauthenticated/request-reset")({
  component: RequestReset,
});

function RequestReset() {
  const {t} = useTranslation("common");
  const router = useRouter();
  const isTall = useMedia("(min-height: 1200px)");
  const {countdown, isCountdownFinished, reset, isRunning} = useCountdown({from: 30});
  const [state, setValue, validate] = useZodState(z.object({email: z.email("invalidEmail")}), {email: ""});

  const {mutate: sendRequest, isPending} = useMutation({
    meta: {errorMessage: t("emailCouldNotBeSent"), successMessage: t("passwordResetEmailSent")},
    mutationKey: USER_KEYS.mUserReset,
    mutationFn: () => client.post(USER_KEYS.mUserReset[0], {email: state.email}),
    onSuccess: () => {
      reset({shouldStartCountdown: true});
    },
  });

  const handleRequestPasswordReset = async () => {
    if (validate()) {
      sendRequest();
    }
  };

  useKeyPressEvent("Enter", () => handleRequestPasswordReset());
  useTitle(t("Reset Password"));

  return (
    <div className="size-full px-4 sm:px-8 md:px-12">
      <div
        className={
          "flex size-full flex-col items-center justify-center pb-16 sm:px-20 lg:px-8 xl:px-20 " +
          (isTall ? "gap-16" : "gap-10")
        }>
        <div className="flex w-full justify-between">
          <button onClick={() => router.history.back()}>
            <RightArrowIcon className="text-primary-sky size-4 shrink-0 rotate-180" />
          </button>
          <span className="font-domaine-display text-primary-midnight text-center text-5xl font-bold">
            {t("Reset Password")}
          </span>
          <div className="w-4.25" />
        </div>
        <div className="mb-5 size-20 shrink-0">
          <img src={RefluencedLogo} className="size-full object-contain" />
        </div>
        <span className="text-neutral-black text-center">{t("requestPasswordResetDescription")}</span>
        <InputField
          onChange={newValue => setValue("email", newValue)}
          value={state.email}
          placeholder={t("email")}
          label={t("email")}
          type="email"
          error={state.errors.email ? t(state.errors.email as "invalidEmail") : null}
        />
        {!!state.errors.overall && (
          <span className="text-danger-medium-red mt-0.5 text-xs">{t("an_error_occurred")}</span>
        )}

        <div className="flex flex-col items-center gap-5 font-medium">
          {isRunning && <span>{t("waitXToTryAgain", {seconds: countdown})}</span>}
          <Button
            onClick={handleRequestPasswordReset}
            isLoading={isPending}
            isDisabled={!isCountdownFinished && isRunning}
            className="h-14 w-48"
            buttonStyle={"Gradient"}>
            {t("send")}
          </Button>

          <div className="flex gap-1.5">
            <span className="text-neutral-black font-medium">{t("furtherHelp")}</span>
            <TextButton
              onClick={() => {
                window.location.href = `mailto:info@refluenced.com?subject=Passwort Reset`;
              }}
              text="Refluenced Support"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
